/* boxart.js — drag to rotate, drag up to zoom, settle back to the default view.
 *
 * Pairs with snippets/boxart.liquid and assets/boxart.css. Self-initialising:
 * every [data-boxart] on the page is wired on DOMContentLoaded. Sections that
 * inject a box later can call window.FGBoxart.init(container) themselves; roots
 * already wired are skipped, so calling it twice is harmless.
 */
(function() {
  'use strict';

  function setup(root) {
    if (root.dataset.boxartReady) return;
    root.dataset.boxartReady = '1';

    // No side faces means nothing to rotate to; the snippet marks those static
    // and leaves out the hint rather than promising a rotation that won't come.
    if (root.classList.contains('boxart--static')) return;

    var outer = root.querySelector('.boxart__outer');   // pointer target
    var scene = root.querySelector('.boxart__scene');   // owns rest pose + zoom
    var box   = root.querySelector('.boxart__box');     // the rotating cube
    if (!outer || !scene || !box) return;

    var shadow = root.querySelector('.boxart__shadow');
    var hint  = root.querySelector('[data-boxart-hint]');
    var ghost = root.querySelector('[data-boxart-ghost]');

    var REST = 30;              // resting angle the CSS starts from
    var DEG_PER_PX = 0.45;
    // Dragging down pulls the box back a little before it bottoms out. It is
    // not useful on its own, but without it a downward drag does nothing at
    // all, and the usual first guess is to drag down -- the small pullback is
    // what tells you the axis is live and that up is the interesting way.
    // Zooming in is bounded by what you can actually see, not by a fixed
    // number: the box grows until it fills FIT of the usable viewport, so it
    // never runs off the bottom or hides under a sticky header. ZOOM_CEIL is
    // only a sanity stop for very large viewports.
    var ZOOM_MIN = 0.88, ZOOM_REST = 1, ZOOM_CEIL = 3;
    // How much of the viewport a fully framed box is allowed to fill. On a
    // phone the box is small to begin with and the screen is the only place to
    // read the back of the box, so it gets the full width; a mouse has a big
    // screen already, and a box bleeding to both edges there just feels loud.
    var coarsePointer = !!(window.matchMedia &&
      window.matchMedia('(pointer: coarse)').matches);
    var FIT_W = coarsePointer ? 1 : 0.9;
    var FIT_H = coarsePointer ? 0.96 : 0.9;
    // How far you drag for the full effect: up to frame it, down to pull back.
    var FOCUS_PX = 300, PULL_PX = 90;
    // Must match .boxart__scene's transform in boxart.css: the zoom is composed
    // from these, so a change there has to be mirrored here.
    var RESTING_SCALE = 0.9, RESTING_SHIFT = '2%';

    var angle = REST, zoom = 1;
    var startX = 0, startY = 0, startAngle = REST;
    var moved = 0, dragging = false, hovering = false, inView = true;
    var rafId = null, returnRaf = null, resumeTimer = null;
    var phase = 0, lastTs = 0, idleBase = REST;
    var baseCx = null, baseCy = null;   // the box's centre at rest, in viewport px
    var baseW = 0, baseH = 0;           // and its size at rest
    var safeTop = 0, safeBottom = 0;    // viewport covered by sticky chrome
    var zoomMax = 2.3;                  // recomputed per drag, see measureBase
    // Vertical drag distance, and the 0..1 "framed" progress it drives.
    // Centring runs off focus rather than off zoom, because zoomMax can land
    // below 1 on a short viewport -- there, framing the box means shrinking it
    // slightly, and a zoom-derived progress cannot express that.
    var drive = 0, startDrive = 0, focus = 0;
    // Touch bookkeeping. Every live pointer is tracked so two of them can be
    // read as a pinch; `touchDrag` flips the vertical sense, because on a
    // phone dragging DOWN reads as pulling the box toward you, the opposite
    // of the mouse convention.
    var pointers = {}, pointerCount = 0, touchDrag = false;
    var pinching = false, pinchStartDist = 0, pinchStartFocus = 0, pinchIds = null;
    var PINCH_GAIN = 1.5;

    var reduceMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // The box's footprint, read from the same custom properties the faces use.
    var facePct = 100, depthPct = 25;
    (function readShape() {
      var cs = window.getComputedStyle(scene);
      var f = parseFloat(cs.getPropertyValue('--box-width'));
      var d = parseFloat(cs.getPropertyValue('--box-depth'));
      if (!isNaN(f)) facePct = f;
      if (!isNaN(d)) depthPct = d;
    })();

    // Light is at the viewer's eye, so the shadow is the box's silhouette
    // thrown straight back. Turning the box changes how wide that silhouette
    // is -- face-on it is just the face, edge-on it is just the depth -- and
    // lifting it toward the viewer makes the shadow bigger and softer.
    function paintShadow(animate) {
      if (!shadow) return;
      var a = angle * Math.PI / 180;
      var w = facePct * Math.abs(Math.cos(a)) + depthPct * Math.abs(Math.sin(a));
      var lift = focus;
      shadow.style.transition = animate
        ? 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1), filter 0.55s, opacity 0.55s'
        : 'none';
      shadow.style.width = w.toFixed(2) + '%';
      // Bigger than the box, always: the box sits between the eye-light and
      // the backdrop, so what lands behind it is magnified. At the same size
      // the shadow just hides behind the artwork. It spreads further as the
      // box is lifted, which is what sells the lift.
      // A touch of lateral lean and drop, so it reads as thrown rather than
      // pasted on -- the light is at the eye but a little above it.
      shadow.style.transform = 'translate(-50%, 0) translate(' +
        (Math.sin(a) * depthPct * 0.18).toFixed(2) + '%, ' +
        (2 + 4 * lift).toFixed(2) + '%) scale(' +
        (1.07 + 0.22 * lift).toFixed(3) + ')';
      shadow.style.filter = 'blur(' + (26 + 50 * lift).toFixed(1) + 'px)';
      shadow.style.opacity = (0.55 + 0.2 * lift).toFixed(3);
    }

    function apply(deg, animate) {
      box.style.transition = animate ? 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
      box.style.transform = 'rotateY(' + deg + 'deg)';
      paintShadow(animate);
    }

    // A stacking context anywhere above the box would trap it under the site's
    // sticky bars, so the ancestors are raised too while it is framed, and put
    // back exactly as they were afterwards.
    var lifted = [];
    function liftAbovePage(on) {
      if (on) {
        if (lifted.length) return;
        var el = root.parentElement;
        while (el && el !== document.body) {
          lifted.push([el, el.style.zIndex, el.style.position]);
          if (window.getComputedStyle(el).position === 'static') el.style.position = 'relative';
          el.style.zIndex = '2147483000';
          el = el.parentElement;
        }
      } else {
        for (var i = 0; i < lifted.length; i++) {
          lifted[i][0].style.zIndex = lifted[i][1];
          lifted[i][0].style.position = lifted[i][2];
        }
        lifted = [];
      }
      root.classList.toggle('is-focused', !!on);
    }

    function restingTransform() {
      return 'scale(' + RESTING_SCALE + ') translate(0, ' + RESTING_SHIFT + ')';
    }

    // How much of the viewport is covered by sticky/fixed chrome, top and
    // bottom. Found by probing the stack of elements at the very edge and
    // stepping past each full-width sticky thing it finds, so a stacked bar
    // (site header + section nav) is measured as one block. The product page
    // has both of those AND a floating cart bar along the bottom, none of
    // which the component can know about by name.
    function stickyInsets() {
      var mid = Math.round(window.innerWidth / 2);
      var h = window.innerHeight;

      function scan(fromTop) {
        var edge = 0;
        for (var pass = 0; pass < 6; pass++) {
          var y = fromTop ? edge + 1 : h - edge - 1;
          if (y < 1 || y > h - 1) break;
          var stack = document.elementsFromPoint(mid, y) || [];
          var grew = false;
          for (var i = 0; i < stack.length; i++) {
            var el = stack[i];
            if (el === document.body || el === document.documentElement) continue;
            var pos = window.getComputedStyle(el).position;
            if (pos !== 'fixed' && pos !== 'sticky') continue;
            var r = el.getBoundingClientRect();
            // Full-width bars only; a sticky side rail shouldn't count.
            if (r.width < window.innerWidth * 0.5) continue;
            var reach = fromTop ? r.bottom : h - r.top;
            // Cap it: something sticky and nearly full-height is not chrome.
            if (reach > edge && reach < h * 0.4) { edge = reach; grew = true; }
          }
          if (!grew) break;
        }
        return edge;
      }

      return { top: scan(true), bottom: scan(false) };
    }

    // Measured with the resting transform applied, so the reading isn't fed
    // back through whatever zoom is already in place.
    function measureBase() {
      var prev = scene.style.transform;
      scene.style.transition = 'none';
      scene.style.transform = restingTransform();
      var r = scene.getBoundingClientRect();
      baseCx = r.left + r.width / 2;
      baseCy = r.top + r.height / 2;
      baseW = r.width;
      baseH = r.height;
      scene.style.transform = prev;

      // The whole viewport, not the band under the page's sticky bars: while
      // framed the box is raised above them (see liftAbovePage), so it reads
      // as coming toward you past the chrome rather than being clipped by it.
      // stickyInsets still tells us how much chrome there is, so the box can
      // be nudged clear of it if the lift is ever defeated.
      var inset = stickyInsets();
      safeTop = inset.top;
      safeBottom = inset.bottom;
      var usableH = window.innerHeight;
      var usableW = window.innerWidth;
      // The scene is square and the box fills its height, so the scene's rect
      // is the box's bounding box -- erring toward under-zooming for a box
      // narrower than its scene, which is the safe direction.
      var fit = Math.min(usableW * FIT_W / Math.max(1, baseW),
                         usableH * FIT_H / Math.max(1, baseH));
      // No floor above 1: on a short viewport the box already overflows the
      // visible band at rest, and forcing a zoom-in there is exactly how it
      // ends up cut off by the header. Framing may legitimately shrink it.
      zoomMax = Math.max(0.5, Math.min(ZOOM_CEIL, fit));
    }

    // Centre of the viewport, biased a little away from whichever edge
    // carries more chrome, so the box does not sit awkwardly under a tall
    // header even while floating above it.
    function safeCentreY() {
      return window.innerHeight / 2 + (safeTop - safeBottom) * 0.25;
    }

    // Zoom grows the box and walks it toward the middle of the viewport, so a
    // close look lands centred no matter where the page is scrolled to.
    function applyZoom(animate) {
      // Walk toward the viewport centre only while zooming IN. Keyed off the
      // rest zoom rather than ZOOM_MIN, or the pullback range would drag the
      // box off-centre at rest.
      var k = focus;
      var dx = 0, dy = 0;
      if (k > 0) {
        if (baseCx === null) measureBase();
        dx = (window.innerWidth / 2 - baseCx) * k;
        dy = (safeCentreY() - baseCy) * k;
      }
      liftAbovePage(focus > 0.02);
      paintShadow(animate);
      scene.style.transition = animate ? 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
      scene.style.transform =
        'translate(' + dx.toFixed(1) + 'px, ' + dy.toFixed(1) + 'px) ' +
        'scale(' + (RESTING_SCALE * zoom) + ') translate(0, ' + RESTING_SHIFT + ')';
    }

    // Ease back to the default three-quarter view (and default zoom) before the
    // ambient loop picks up, so the drift always starts from the same framing.
    function returnToRest(done) {
      if (returnRaf) { window.cancelAnimationFrame(returnRaf); returnRaf = null; }
      var turns = Math.round((angle - REST) / 360);
      var targetAngle = REST + turns * 360;
      var a0 = angle, z0 = zoom, f0 = focus, d0 = drive, t0 = null;
      if (Math.abs(targetAngle - a0) < 0.5 && Math.abs(z0 - ZOOM_REST) < 0.01 && f0 < 0.01) { if (done) done(); return; }
      var dur = Math.min(2200, 700 + Math.abs(targetAngle - a0) * 4.6);
      function step(ts) {
        if (t0 === null) t0 = ts;
        var k = Math.min(1, (ts - t0) / dur);
        var e = 1 - Math.pow(1 - k, 3);
        angle = a0 + (targetAngle - a0) * e;
        zoom = z0 + (ZOOM_REST - z0) * e;
        focus = f0 * (1 - e);
        drive = d0 * (1 - e);
        apply(angle, false);
        applyZoom(false);
        if (k < 1) {
          returnRaf = window.requestAnimationFrame(step);
        } else {
          returnRaf = null;
          angle = targetAngle;
          zoom = ZOOM_REST;
          focus = 0;
          drive = 0;
          applyZoom(false);
          liftAbovePage(false);
          if (done) done();
        }
      }
      returnRaf = window.requestAnimationFrame(step);
    }

    // Ambient drift is driven here rather than by a CSS animation so it can
    // resume from wherever the box was left, instead of snapping back to the
    // keyframe's start angle.
    function idleStep(ts) {
      if (!lastTs) lastTs = ts;
      phase += (ts - lastTs) / 1000;
      lastTs = ts;
      angle = idleBase + Math.sin(phase * 0.6) * 9;
      apply(angle, false);
      rafId = window.requestAnimationFrame(idleStep);
    }

    function startIdle() {
      if (reduceMotion || rafId || dragging || hovering || !inView) return;
      if (document.hidden) return;
      idleBase = angle;
      phase = 0;
      lastTs = 0;
      rafId = window.requestAnimationFrame(idleStep);
    }

    function stopIdle() {
      if (rafId) { window.cancelAnimationFrame(rafId); rafId = null; }
      if (returnRaf) { window.cancelAnimationFrame(returnRaf); returnRaf = null; }
      window.clearTimeout(resumeTimer);
    }

    // Settle back to the default view first, then drift from there.
    function scheduleIdle(delay) {
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(function() {
        if (dragging || hovering) return;
        returnToRest(startIdle);
      }, delay);
    }

    function release() {
      if (!dragging) return;
      dragging = false;
      root.classList.remove('is-dragging');
      if (moved < 6) {
        // Treated as a tap: flip front <-> back.
        var facing = ((angle - REST) % 360 + 360) % 360;
        angle = facing < 90 || facing > 270 ? REST + 180 : REST;
      } else if (focus > 0.12) {
        // Zoomed in to read something, so settle face-on rather than back to
        // the three-quarter view — flat to the front, back or either side.
        angle = Math.round(angle / 90) * 90;
      } else {
        // Settle on the nearest three-quarter view.
        angle = Math.round((angle - REST) / 90) * 90 + REST;
      }
      apply(angle, true);
      // A pullback springs straight back: it is a "nothing down here" signal,
      // not a view anyone wants to hold.
      if (drive < 0) {
        drive = 0;
        focus = 0;
        zoom = ZOOM_REST;
        applyZoom(true);
      }
      // No auto-return while the pointer is still on the box: letting go is
      // not the same as being done with it, and a deliberate turn to the back
      // should stay put while they are still looking. Taking the pointer off
      // the box, or scrolling away, is what hands the view back.
    }

    outer.addEventListener('pointerenter', function() {
      hovering = true;
      stopIdle();
    });

    // While framed, the box has been moved to the middle of the viewport and
    // is far bigger than the column it lives in, so .boxart__outer's own rect
    // is no longer where the box appears. Hover has to be judged against the
    // scene's current on-screen rect, or letting go anywhere on the enlarged
    // box reads as leaving it and snaps the view away.
    function pointerOverBox(e) {
      var r = scene.getBoundingClientRect();
      return e.clientX >= r.left && e.clientX <= r.right &&
             e.clientY >= r.top && e.clientY <= r.bottom;
    }

    outer.addEventListener('pointerleave', function(e) {
      if (focus > 0.02 && pointerOverBox(e)) return;
      hovering = false;
      release();
      scheduleIdle(600);
    });

    // The counterpart: once framed, leaving the box's visible bounds is what
    // hands the view back, wherever the original column happens to be.
    document.addEventListener('pointermove', function(e) {
      if (dragging || focus <= 0.02) return;
      var over = pointerOverBox(e);
      hovering = over;
      if (over) window.clearTimeout(resumeTimer);
      else scheduleIdle(250);
    });

    // Measured between the two pointers that actually started the pinch, not
    // whichever two happen to be in the map -- a stale pointer (a mouse button
    // still held, a lost touch) would otherwise be read as one of the fingers.
    function spread() {
      if (!pinchIds) return 0;
      var a = pointers[pinchIds[0]], b = pointers[pinchIds[1]];
      if (!a || !b) return 0;
      return Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y));
    }

    // Re-anchor the one-finger drag to a pointer, so lifting one finger out of
    // a pinch continues as a drag instead of jumping.
    function anchorDrag(e) {
      startX = e.clientX;
      startY = e.clientY;
      startAngle = angle;
      startDrive = drive;
    }

    outer.addEventListener('pointerdown', function(e) {
      // Without this the browser starts its own image drag and the box never
      // sees the move events.
      e.preventDefault();
      if (!pointers[e.pointerId]) pointerCount++;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY, touch: e.pointerType === 'touch' };
      // Two touches make a pinch. A mouse never pinches, so it is ignored
      // here and keeps its one-pointer drag.
      if (!pinching && e.pointerType === 'touch') {
        var touchIds = Object.keys(pointers).filter(function(id) {
          return pointers[id].touch;
        });
        if (touchIds.length >= 2) {
          pinchIds = [touchIds[touchIds.length - 2], touchIds[touchIds.length - 1]];
          pinching = true;
          pinchStartDist = spread();
          pinchStartFocus = focus;
          moved = 999;          // a pinch is never a tap
        }
      }
      dragging = true;
      touchDrag = e.pointerType === 'touch';
      moved = pinching ? 999 : 0;
      anchorDrag(e);
      measureBase();      // the page may have scrolled since the last drag
      stopIdle();
      if (hint) hint.classList.add('is-done');
      if (ghost) ghost.classList.add('is-done');
      root.classList.add('is-dragging');
      if (outer.setPointerCapture) {
        try { outer.setPointerCapture(e.pointerId); } catch (err) { /* older browsers */ }
      }
    });

    outer.addEventListener('pointermove', function(e) {
      if (!dragging) return;
      e.preventDefault();

      if (pointers[e.pointerId]) {
        pointers[e.pointerId].x = e.clientX;
        pointers[e.pointerId].y = e.clientY;
      }

      // Two fingers: spread to frame it, squeeze to let it go. Rotation stays
      // a one-finger gesture, so a pinch never spins the box by accident.
      if (pinching) {
        var now = spread();
        if (pinchStartDist > 0 && now > 0) {
          var ratio = now / pinchStartDist;
          focus = Math.max(0, Math.min(1, pinchStartFocus + (ratio - 1) * PINCH_GAIN));
          drive = focus * FOCUS_PX;     // keep a later one-finger drag continuous
          zoom = ZOOM_REST + (zoomMax - ZOOM_REST) * focus;
          applyZoom(false);
        }
        return;
      }

      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      moved = Math.max(moved, Math.abs(dx), Math.abs(dy));
      angle = startAngle + dx * DEG_PER_PX;

      // Pull up to frame the box in the viewport, push down to pull back.
      // Down has far less range than up -- just enough to register as a
      // response, so the axis announces itself to someone whose first guess
      // was to drag downward.
      // Vertical drag frames the box. Touch inverts it: on a phone dragging
      // DOWN reads as pulling the box toward you, where a mouse drags up.
      // This is reliable now only because the scene takes touch-action: none
      // -- while the browser could still claim a vertical pan it would cancel
      // the drag part-way and the gesture would die at random.
      drive = Math.max(-PULL_PX, Math.min(FOCUS_PX,
        startDrive + (touchDrag ? dy : -dy)));
      if (drive >= 0) {
        focus = drive / FOCUS_PX;
        zoom = ZOOM_REST + (zoomMax - ZOOM_REST) * focus;
      } else {
        focus = 0;
        zoom = ZOOM_REST + (ZOOM_MIN - ZOOM_REST) * (-drive / PULL_PX);
      }
      apply(angle, false);
      applyZoom(false);
    });

    function endPointer(e) {
      if (pointers[e.pointerId]) {
        delete pointers[e.pointerId];
        pointerCount = Math.max(0, pointerCount - 1);
      }
      if (pinching && (!pinchIds || !pointers[pinchIds[0]] || !pointers[pinchIds[1]])) {
        pinching = false;
        pinchIds = null;
        // Carry on as a one-finger drag from whichever finger is still down.
        var ids = Object.keys(pointers);
        if (ids.length) {
          anchorDrag({ clientX: pointers[ids[0]].x, clientY: pointers[ids[0]].y });
          return;
        }
      }
      if (pointerCount === 0) release();
    }

    outer.addEventListener('pointerup', endPointer);
    outer.addEventListener('pointercancel', endPointer);
    outer.addEventListener('dragstart', function(e) { e.preventDefault(); });

    // Don't burn frames on a box that's scrolled away or on a hidden tab.
    if (window.IntersectionObserver) {
      new IntersectionObserver(function(entries) {
        inView = entries[0].isIntersecting;
        if (inView) startIdle(); else stopIdle();
      }, { threshold: 0.1 }).observe(outer);
    }
    document.addEventListener('visibilitychange', function() {
      if (document.hidden) stopIdle(); else scheduleIdle(300);
    });

    // Scrolling while framed would drag the box across the sticky bars it is
    // currently sitting on top of, so any scroll hands the page back.
    window.addEventListener('scroll', function() {
      if (dragging || focus <= 0.02) return;
      stopIdle();
      returnToRest(startIdle);
    }, { passive: true });

    startIdle();
  }

  function init(container) {
    (container || document).querySelectorAll('[data-boxart]').forEach(setup);
  }

  window.FGBoxart = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { init(); });
  } else {
    init();
  }
})();
